import { call, put, select, takeLatest } from "redux-saga/effects";

import * as userTypes from "Store/user/constants";
import * as userActions from "Store/user/actions";
import { setCurrentUser, setUserIsLogged } from "Store/user/actions";

import {
  changePasswordApi,
  fetchCurrentUserApi,
  impersonateApi,
  loginApi,
  LogoutApi,
  resetPasswordApi,
  updateInstructorAvatarApi,
  updateLastVersionSeenApi
} from "Store/user/api";
import { makeSelectCurrentUser } from "Store/user/selectors";
import {
  handleFetchCourses,
  handleFetchLessonsFilterItems,
  handleFetchSubjects,
  handleFetchWorksheetsFilterItems,
  setActionSpinner
} from "Store/global/actions";
import { generateToastFromObject, Toast } from "utils/toast";
import ERRORS from "constants/localization/errors";
import SUCCESSES from "constants/localization/successes";
import Router from "next/router";
import { logout, setToken } from "Store/user/AuthService";
import { fetchCurrentLocation } from "Store/locations/actions";
import { capitalizeFirstLetter } from "utils/textHelpers";
import {
  handleUpdateInstructorAddress,
  handleUpdateInstructorInfo
} from "Store/instructors/actions";
import { deepEqual, rewriteTheSameProperties } from "utils/objectsHelper";
import { instructorInfoInit } from "utils/initialObjects";
import { handleFetchCoursesSaga } from "Store/global/saga";

function* handleFetchCurrentUser() {
  try {
    const { error, data } = yield fetchCurrentUserApi();

    if (error) {
      console.error(ERRORS.handleFetchCurrentUser, " - error from fetchCurrentUserApi", error);
      logout();
    }

    if (data) {
      if (data.type === "STUDENT") {
        window && window.location && window.location.replace(`${window.location.origin}`);
      }
      yield put(userActions.setCurrentUser(data));
      yield call(handleFetchCoursesSaga);
      yield put(fetchCurrentLocation());
      // get all required filters options from backend
      yield put(handleFetchWorksheetsFilterItems());
      yield put(handleFetchLessonsFilterItems());
      yield put(handleFetchSubjects());

      yield put(userActions.setAuthorizedStatus(true));
    } else {
      yield put(userActions.setAuthorizedStatus(false));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchCurrentUser, error);
  }
}

function* handleFetchOnlyCurrentUser() {
  try {
    const { error, data } = yield fetchCurrentUserApi();

    if (error) {
      console.error(ERRORS.handleFetchCurrentUser, " - error from fetchCurrentUserApi", error);
      logout();
    }

    if (data) {
      yield put(userActions.setCurrentUser(data));
    } else {
      yield put(userActions.setAuthorizedStatus(false));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchCurrentUser, error);
  }
}


function* handleChangePassword(action) {
  try {
    const { error, status } = yield changePasswordApi(action.payload);
    if (error) {
      switch (status) {
        case 400:
          Toast.error(ERRORS.handleChangePasswordInvalidToken, error);
          break;
        case 422:
          Toast.error(ERRORS.handleChangePasswordValidationError, error);
          break;
        default:
          Toast.error(ERRORS.handleChangePassword, error);
          break;
      }
    }
    setTimeout(() => Router.push("/login"), 3000);
    Toast.success(SUCCESSES.handleChangePassword);
  } catch (error) {
    Toast.error(ERRORS.handleChangePassword, error);
  }
}

function* handleResetPassword(action) {
  try {
    const { error, status } = yield resetPasswordApi(action.email);
    if (error) {
      Toast.error(
        status === 404 ? ERRORS.handleResetPasswordInvalidEmail : ERRORS.handleResetPassword,
        error
      );
    }
    Toast.success(SUCCESSES.handleResetPassword);
  } catch (error) {
    Toast.error(ERRORS.handleResetPassword, error);
  }
}

function* handleImpersonate(action) {
  yield put(setActionSpinner(true));
  try {
    const { item, type } = action.payload;
    const { data, error } = yield impersonateApi(item.id);

    if (error) {
      Toast.error(ERRORS.handleImpersonate);
    } else {
      const baseUrl = item?.locations[0]?.urls?.[type === "student" ? "student" : "admin"];
      if (!baseUrl)
        Toast.error(ERRORS.handleImpersonateInvalidLocation(capitalizeFirstLetter(type)));

      const currentUser = yield select(makeSelectCurrentUser);
      const { avatar, first_name, last_name } = currentUser;

      const impersonatingString = avatar
        ? `impersonateAvatar=${avatar}`
        : `impersonateName=impersonate-name-${first_name}%20${last_name}`;
      window.open(
        `${baseUrl}/dashboard?expires_at=${data.expires_at}&auth=${data.token}&${impersonatingString}`,
        "_blank",
        "noopener"
      );
    }
  } catch (error) {
    Toast.error(ERRORS.handleImpersonate, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleUpdateLastVersionSeen(action) {
  try {
    yield updateLastVersionSeenApi(action.payload);
    const user = yield select(makeSelectCurrentUser);

    const updatedUser = {
      ...user,
      last_version_seen: action.payload.last_version_seen
    };

    yield put(userActions.setCurrentUser(updatedUser));
  } catch (error) {
    Toast.error(ERRORS.handleUpdateLastVersionSeen, error.message || "Something went wrong.");
  }
}

function* logoutSaga() {
  yield put(setActionSpinner(true));
  try {
    if (localStorage.getItem("adminToken")) {
      const { error, status } = yield LogoutApi();
      if (error && status !== 401) {
        Toast.error(ERRORS.logoutSaga, error);
        return;
      }
    }

    yield Router.push("/login");

    logout();
    yield put(setCurrentUser(null));
    yield put(setUserIsLogged(false));
    yield window?.location?.replace(`${window?.location.origin}/login`);

  } catch (error) {
    Toast.error(ERRORS.logoutSaga, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* loginSaga(action) {
  yield put(setActionSpinner(true));
  try {
    const { data, error } = yield loginApi(action.payload);
    if (error) {
      generateToastFromObject(error, "");
    } else {
      const env = process.env.BUILD;
      if (data && data.intended_origin && data.user) {
        const { token, expires_at, intended_origin, user } = data;

        window?.location?.replace(
          `${intended_origin}${
            user.type !== "STUDENT" && env !== "dev" ? "/admin" : ""
          }/dashboard?expires_at=${expires_at}auth=${token}`
        );
      } else if (data.token && data.expires_at) {
        if (data.user && data.user.type === "STUDENT") {
          setToken(data.token, data.expires_at, "student");
          yield call(handleFetchCurrentUser);
        } else {
          setToken(data.token, data.expires_at, "admin");
          window?.location?.replace(`${window.location.origin}/${env !== "dev" ? "admin/" : ""}`);
        }
      }
    }
  } catch (error) {
    generateToastFromObject(error, "");
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleUpdateUserAvatarSaga(action) {
  try {
    const { error } = yield call(updateInstructorAvatarApi, action.payload);
    if (error) {
      return Toast.error(ERRORS.handleUpdateInstructorAvatar, error);
    } else {
      Toast.success(SUCCESSES.handleUpdateInstructorAvatar);
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateInstructorAvatar, error);
  }
}

function* handleUpdateUserProfileSaga(action: userTypes.IHandleUpdateUserProfile) {
  try {
    yield put(setActionSpinner(true));
    const currentUser = yield select(makeSelectCurrentUser);
    const form = new FormData();
    const { avatar, user } = action.payload;
    const instructorInfo = rewriteTheSameProperties(
      instructorInfoInit,
      user
    ) as UpdateInstructorInfoRequest;
    const isAddressUpdated = deepEqual(currentUser?.addresses, user?.addresses);
    if (avatar) {
      form.append("user_id", user.id);
      form.append("avatar", avatar);
      yield call(handleUpdateUserAvatarSaga, { payload: form });
    }
    yield put(handleUpdateInstructorInfo({ ...instructorInfo, user_id: user.id }));
    if (!isAddressUpdated) {
      yield put(handleUpdateInstructorAddress({ ...user.addresses, user_id: user.id }));
    }
    yield put(setActionSpinner(false));
    yield call(handleFetchCurrentUser);
  } catch (error) {
    yield put(setActionSpinner(false));
  }
}

export default function* usersSaga() {
  yield takeLatest(userTypes.HANDLE_FETCH_CURRENT_USER, handleFetchCurrentUser);
  yield takeLatest(userTypes.HANDLE_FETCH_ONLY_CURRENT_USER, handleFetchOnlyCurrentUser);
  yield takeLatest(userTypes.HANDLE_CHANGE_PASSWORD, handleChangePassword);
  yield takeLatest(userTypes.HANDLE_REQUEST_RESET_PASSWORD, handleResetPassword);
  yield takeLatest(userTypes.HANDLE_UPDATE_USER_AVATAR, handleUpdateUserAvatarSaga);
  yield takeLatest(userTypes.HANDLE_IMPERSONATE, handleImpersonate);
  yield takeLatest(userTypes.HANDLE_UPDATE_LAST_VERSION_SEEN, handleUpdateLastVersionSeen);
  yield takeLatest(userTypes.HANDLE_LOGOUT, logoutSaga);
  yield takeLatest(userTypes.HANDLE_LOGIN, loginSaga);
  yield takeLatest(userTypes.HANDLE_UPDATE_USER_PROFILE, handleUpdateUserProfileSaga);
}
